import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";
import { publicApi, API } from "@/lib/api";
import Logo from "@/components/Logo";

export default function InquiryFlow() {
  const [step, setStep] = useState(1); // 1 = info, 2 = menu, 3 = done
  const [info, setInfo] = useState({ name: "", phone: "", email: "", event_date: "", event_time: "", notes: "" });
  const [menu, setMenu] = useState({ categories: [] });
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`${API}/public/menu`);
        setMenu(data);
      } finally { setLoadingMenu(false); }
    })();
  }, []);

  function nextStep() {
    if (!info.name.trim()) return toast.error("Please enter your name");
    if (!info.phone.trim() && !info.email.trim()) return toast.error("Please add a phone or email so we can reach you");
    if (!info.event_date) return toast.error("Please pick an event date");
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleItem(name) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  async function submit() {
    setSubmitting(true);
    try {
      await publicApi.post(`${API}/public/inquiries`, {
        ...info,
        selected_items: Array.from(selected),
      });
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit inquiry");
    } finally {
      setSubmitting(false);
    }
  }

  const filteredCategories = search
    ? menu.categories
        .map((c) => ({ ...c, items: c.items.filter((it) => it.name.toLowerCase().includes(search.toLowerCase())) }))
        .filter((c) => c.items.length > 0)
    : menu.categories;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="inquiry-page">
      <header className="px-5 sm:px-10 py-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9963F] text-xs uppercase tracking-[0.2em]">
          <ArrowLeft size={14} /> Home
        </Link>
        <Logo size="sm" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
          {step === 1 ? "Step 1 of 2" : step === 2 ? "Step 2 of 2" : "Done"}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 sm:px-10 py-12">
        {/* Progress */}
        {step !== 3 && (
          <div className="flex items-center gap-3 mb-12">
            {[1, 2].map((s) => (
              <div key={s} className="flex-1 flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center text-xs font-medium ${
                  step >= s ? "bg-[#C9963F] text-black" : "bg-[#0e0e0e] border border-white/10 text-white/50"
                }`}>{s}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-white/60">{s === 1 ? "Your info" : "Pick menu"}</div>
                {s < 2 && <div className={`flex-1 h-px ${step > s ? "bg-[#C9963F]" : "bg-white/10"}`} />}
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="fade-up">
            <h1 className="font-serif-brand text-4xl sm:text-5xl mb-3">Tell us about your event.</h1>
            <p className="text-white/50 mb-10 text-sm">We'll use this to send you a personalized quote.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Name *" full>
                <input data-testid="inq-name" autoFocus className={lineCls} value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} />
              </Field>
              <Field label="Phone *">
                <input data-testid="inq-phone" className={lineCls} value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="(214) 555-0123" />
              </Field>
              <Field label="Email *">
                <input data-testid="inq-email" type="email" className={lineCls} value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
              </Field>
              <Field label="Event Date *">
                <input data-testid="inq-date" type="date" className={boxCls} value={info.event_date} onChange={(e) => setInfo({ ...info, event_date: e.target.value })} />
              </Field>
              <Field label="Event Time">
                <input data-testid="inq-time" type="time" className={boxCls} value={info.event_time} onChange={(e) => setInfo({ ...info, event_time: e.target.value })} />
              </Field>
              <Field label="Anything else? (optional)" full>
                <textarea rows={3} className={boxCls} value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} placeholder="Guest count, location, special requests…" />
              </Field>
            </div>

            <div className="mt-10 flex justify-end">
              <button onClick={nextStep} data-testid="inq-next-btn"
                className="bg-[#C9963F] text-black font-medium px-7 py-3 hover:bg-[#A67A2D] inline-flex items-center gap-2 text-sm">
                Continue to menu <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="fade-up">
            <h1 className="font-serif-brand text-4xl sm:text-5xl mb-3">Pick your menu.</h1>
            <p className="text-white/50 mb-6 text-sm">Tap items you'd like featured. Don't worry — pricing is built into your custom quote.</p>

            {/* Search + counter */}
            <div className="sticky top-0 bg-[#050505] py-3 z-10 mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search the menu…"
                  className="w-full bg-[#0e0e0e] border border-white/10 pl-9 pr-3 py-2 text-sm text-white focus:border-[#C9963F] focus:outline-none" />
              </div>
              <div className="text-xs uppercase tracking-wider text-white/60 px-3 py-2 border border-[#C9963F]/30 bg-[#C9963F]/5 text-[#C9963F] text-center sm:text-left" data-testid="selection-count">
                {selected.size} item{selected.size !== 1 ? "s" : ""} selected
              </div>
            </div>

            {loadingMenu && <div className="text-white/40 text-sm">Loading menu…</div>}

            <div className="space-y-8">
              {filteredCategories.map((cat) => (
                <section key={cat.name}>
                  <div className="flex items-baseline gap-4 mb-3">
                    <h2 className="font-serif-brand text-2xl text-[#C9963F] uppercase tracking-wide">{cat.name}</h2>
                    <div className="flex-1 h-px bg-[#C9963F]/30" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {cat.items.map((it) => {
                      const isSel = selected.has(it.name);
                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={() => toggleItem(it.name)}
                          data-testid={`menu-item-${it.id}`}
                          className={`text-left px-3 py-2.5 border text-sm transition-colors flex items-start gap-2 ${
                            isSel
                              ? "border-[#C9963F] bg-[#C9963F]/10 text-white"
                              : "border-white/10 bg-[#0a0a0a] text-white/80 hover:border-white/30"
                          }`}
                        >
                          <span className={`mt-0.5 w-4 h-4 border ${isSel ? "border-[#C9963F] bg-[#C9963F]" : "border-white/30"} flex items-center justify-center shrink-0`}>
                            {isSel && <CheckCircle2 size={11} className="text-black" />}
                          </span>
                          <span className="leading-tight">{it.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            {filteredCategories.length === 0 && !loadingMenu && (
              <div className="text-white/40 text-sm py-12 text-center">No items match "{search}"</div>
            )}

            <div className="mt-12 flex flex-col sm:flex-row gap-3 sm:justify-between">
              <button onClick={() => setStep(1)} className="border border-white/15 text-white/70 hover:border-white/40 px-5 py-3 text-sm">
                ← Back
              </button>
              <button onClick={submit} disabled={submitting} data-testid="inq-submit-btn"
                className="bg-[#C9963F] text-black font-medium px-7 py-3 hover:bg-[#A67A2D] inline-flex items-center justify-center gap-2 text-sm disabled:opacity-50">
                {submitting ? "Submitting…" : `Submit Inquiry${selected.size ? ` (${selected.size})` : ""}`}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="text-center py-20 fade-up" data-testid="inq-success">
            <Sparkles size={32} className="text-[#C9963F] mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-serif-brand text-4xl sm:text-5xl mb-4">Inquiry received.</h1>
            <p className="text-white/60 max-w-md mx-auto mb-10">
              We've got your details, {info.name.split(" ")[0] || "friend"}. Our team will reach out within 24 hours with a tailored quote.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/" className="border border-white/15 text-white/80 hover:border-[#C9963F] hover:text-[#C9963F] px-6 py-3 text-sm">Back to home</Link>
              <Link to="/menu" className="bg-[#C9963F] text-black font-medium px-6 py-3 hover:bg-[#A67A2D] text-sm">Browse full menu</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const lineCls = "w-full bg-transparent border-b border-white/15 px-0 py-2 focus:border-[#C9963F] focus:outline-none text-white";
const boxCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2.5 focus:border-[#C9963F] focus:outline-none text-white";

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</label>
      {children}
    </div>
  );
}
